export const STAFF_PASSWORD_MIN_LENGTH = 12;

export const STAFF_PASSWORD_ERROR_MESSAGES = {
	TOO_SHORT: "ລະຫັດຜ່ານຕ້ອງຢ່າງນ້ອຍ 12 ຕົວອັກສອນ",
	NEEDS_LETTER_AND_NUMBER: "ລະຫັດຜ່ານຕ້ອງມີຕົວອັກສອນ ແລະ ຕົວເລກ",
	PASSWORD_COMPROMISED:
		"ລະຫັດຜ່ານນີ້ຖືກເປີດເຜີຍແລ້ວ ກະລຸນາເລືອກລະຫັດຜ່ານອື່ນ",
	HIBP_CHECK_FAILED: "ບໍ່ສາມາດກວດສອບລະຫັດຜ່ານໄດ້ ກະລຸນາລອງໃໝ່ພາຍຫຼັງ",
} as const;

export type StaffPasswordPolicyCode = keyof typeof STAFF_PASSWORD_ERROR_MESSAGES;

export class StaffPasswordPolicyError extends Error {
	constructor(
		public code: StaffPasswordPolicyCode,
		message: string,
	) {
		super(message);
		this.name = "StaffPasswordPolicyError";
	}
}

export function validateStaffPasswordStrength(password: string): void {
	if (password.length < STAFF_PASSWORD_MIN_LENGTH) {
		throw new StaffPasswordPolicyError(
			"TOO_SHORT",
			STAFF_PASSWORD_ERROR_MESSAGES.TOO_SHORT,
		);
	}
	if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
		throw new StaffPasswordPolicyError(
			"NEEDS_LETTER_AND_NUMBER",
			STAFF_PASSWORD_ERROR_MESSAGES.NEEDS_LETTER_AND_NUMBER,
		);
	}
}

async function sha1HexUpper(input: string): Promise<string> {
	const digest = await crypto.subtle.digest(
		"SHA-1",
		new TextEncoder().encode(input),
	);
	return Array.from(new Uint8Array(digest))
		.map((byte) => byte.toString(16).padStart(2, "0"))
		.join("")
		.toUpperCase();
}

export async function checkStaffPasswordNotCompromised(
	password: string,
): Promise<void> {
	const sha1Hash = await sha1HexUpper(password);
	const prefix = sha1Hash.slice(0, 5);
	const suffix = sha1Hash.slice(5);

	const response = await fetch(
		`https://api.pwnedpasswords.com/range/${prefix}`,
		{
			headers: {
				"Add-Padding": "true",
				"User-Agent": "Mineral Staff Password Checker",
			},
		},
	);

	if (!response.ok) {
		throw new StaffPasswordPolicyError(
			"HIBP_CHECK_FAILED",
			STAFF_PASSWORD_ERROR_MESSAGES.HIBP_CHECK_FAILED,
		);
	}

	const body = await response.text();
	const compromised = body
		.split("\n")
		.some((line) => line.split(":")[0]?.toUpperCase() === suffix);

	if (compromised) {
		throw new StaffPasswordPolicyError(
			"PASSWORD_COMPROMISED",
			STAFF_PASSWORD_ERROR_MESSAGES.PASSWORD_COMPROMISED,
		);
	}
}

export async function assertStaffPasswordPolicy(password: string): Promise<void> {
	validateStaffPasswordStrength(password);
	await checkStaffPasswordNotCompromised(password);
}
